/**
 * SSE (Server-Sent Events) 请求封装
 * 支持 POST 请求的流式数据接收
 */

/**
 * 创建 SSE 连接并处理流式数据
 * @param {Object} options 配置选项
 * @param {string} options.url 请求地址
 * @param {Object} options.data POST 请求的数据
 * @param {Object} options.headers 请求头
 * @param {Function} options.onMessage 接收到消息时的回调函数
 * @param {Function} options.onError 错误处理回调函数
 * @param {Function} options.onOpen 连接打开时的回调函数
 * @param {Function} options.onClose 连接关闭时的回调函数
 * @returns {Function} 返回一个关闭连接的函数
 */
export function createSSEConnection(options) {
  const {
    url,
    data = {},
    headers = {},
    onMessage,
    onError,
    onOpen,
    onClose,
  } = options;

  let abortController = null;
  let isClosed = false;

  // 创建 AbortController 用于取消请求
  abortController = new AbortController();

  // 设置默认请求头
  const defaultHeaders = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "Cache-Control": "no-cache",
    ...headers,
  };

  // 发起 fetch 请求
  fetch(url, {
    method: "POST",
    headers: defaultHeaders,
    body: JSON.stringify(data),
    signal: abortController.signal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 检查响应类型
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("text/event-stream")) {
        console.warn("响应不是 SSE 格式，但继续处理");
      }

      if (onOpen) {
        onOpen();
      }

      // 创建读取流
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // 读取流数据
      function readStream() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              if (onClose && !isClosed) {
                onClose();
              }
              return;
            }

            // 解码数据并添加到缓冲区
            buffer += decoder.decode(value, { stream: true });

            // 处理缓冲区中的完整 SSE 事件
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // 保留最后一个不完整的行

            for (const line of lines) {
              if (line.startsWith("data:")) {
                const dataStr = line.slice(5).trim();
                if (dataStr) {
                  try {
                    // 处理可能的多层 data: 前缀（如 data:data:）
                    let jsonStr = dataStr;
                    while (jsonStr.startsWith("data:")) {
                      jsonStr = jsonStr.slice(5).trim();
                    }

                    // 尝试解析 JSON
                    const eventData = JSON.parse(jsonStr);

                    if (onMessage) {
                      onMessage(eventData);
                    }
                  } catch (error) {
                    // 如果不是 JSON，直接传递原始数据
                    if (onMessage) {
                      onMessage({ data: dataStr });
                    }
                  }
                }
              } else if (line.startsWith("event:")) {
                // 处理事件类型（可选，用于后续扩展）
                // const eventType = line.slice(6).trim();
              } else if (line.startsWith("id:")) {
                // 处理事件 ID（可选）
                // const eventId = line.slice(3).trim();
              } else if (line.startsWith("retry:")) {
                // 处理重试间隔（可选）
                // const retry = parseInt(line.slice(6).trim(), 10);
              }
            }

            // 继续读取
            if (!isClosed) {
              readStream();
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              // 请求被取消，不处理错误
              return;
            }
            if (onError) {
              onError(error);
            }
          });
      }

      // 开始读取流
      readStream();
    })
    .catch((error) => {
      if (error.name === "AbortError") {
        // 请求被取消，不处理错误
        return;
      }
      if (onError) {
        onError(error);
      }
    });

  // 返回关闭连接的函数
  return function close() {
    isClosed = true;
    if (abortController) {
      abortController.abort();
    }
    if (onClose) {
      onClose();
    }
  };
}

/**
 * 简化版的 SSE 请求方法
 * @param {string} url 请求地址
 * @param {Object} data POST 请求的数据
 * @param {Object} callbacks 回调函数对象
 * @returns {Function} 返回关闭连接的函数
 */
export function requestSSE(url, data, callbacks = {}) {
  return createSSEConnection({
    url,
    data,
    ...callbacks,
  });
}
